import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendURL } from '../../utils/backendURL';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Handshake,
  Search,
  Filter,
  Building2,
  Mail,
  Phone,
  Globe,
  Calendar,
  Users,
  Target,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  X,
  ChevronDown,
  RefreshCw,
  MapPin,
  Briefcase
} from 'lucide-react';

const PartnersManagement = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const BACKEND_URL = getBackendURL();
  const token = localStorage.getItem('admin_token');

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterCountry) params.append('country', filterCountry);

      const response = await axios.get(
        `${BACKEND_URL}/api/partners/admin/applications?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/partners/admin/stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchApplications();
    fetchStats();
  }, [filterStatus, filterCountry]);

  const updateApplication = async (applicationId, status) => {
    try {
      setUpdating(true);
      await axios.patch(
        `${BACKEND_URL}/api/partners/admin/applications/${applicationId}`,
        { status, admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApplications();
      fetchStats();
      setSelectedApplication(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Failed to update application:', error);
    } finally {
      setUpdating(false);
    }
  };

  const saveNotes = async (applicationId) => {
    try {
      setUpdating(true);
      await axios.patch(
        `${BACKEND_URL}/api/partners/admin/applications/${applicationId}`,
        { admin_notes: adminNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApplications();
      setSelectedApplication(prev => prev ? { ...prev, admin_notes: adminNotes } : null);
    } catch (error) {
      console.error('Failed to save notes:', error);
    } finally {
      setUpdating(false);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      app.company_name.toLowerCase().includes(search) ||
      app.applicant_name.toLowerCase().includes(search) ||
      app.email.toLowerCase().includes(search) ||
      app.country.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 border-red-200"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  const uniqueCountries = [...new Set(applications.map(a => a.country))].sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-500 rounded-lg flex items-center justify-center">
            <Handshake className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Partner Applications</h1>
            <p className="text-sm text-gray-500">Manage partner program applications</p>
          </div>
        </div>
        <Button onClick={fetchApplications} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Applications</p>
                <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-900">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Approved</p>
                <p className="text-2xl font-bold text-green-900">{stats.approved}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </Card>
          <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Rejected</p>
                <p className="text-2xl font-bold text-red-900">{stats.rejected}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by company, name, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-md text-sm"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              className="h-10 px-3 border border-gray-300 rounded-md text-sm"
            >
              <option value="">All Countries</option>
              {uniqueCountries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Applications Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="h-8 w-8 text-gray-400 mx-auto mb-3 animate-spin" />
            <p className="text-gray-500">Loading applications...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="p-12 text-center">
            <Handshake className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No applications found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Clients</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{app.company_name}</p>
                        <p className="text-sm text-gray-500">{app.industry_focus}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm text-gray-900">{app.applicant_name}</p>
                        <p className="text-sm text-gray-500">{app.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-700">{app.country}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-700">{app.estimated_clients_per_year}/year</span>
                    </td>
                    <td className="px-4 py-4">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-500">
                        {new Date(app.submitted_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedApplication(app);
                          setAdminNotes(app.admin_notes || '');
                        }}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedApplication.company_name}</h2>
                <p className="text-sm text-gray-500">Partner Application Details</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedApplication(null)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-gray-500">Current Status</span>
                  <div className="mt-1">{getStatusBadge(selectedApplication.status)}</div>
                </div>
                {selectedApplication.reviewed_at && (
                  <div className="text-right">
                    <span className="text-sm text-gray-500">Reviewed</span>
                    <p className="text-sm text-gray-700">
                      {new Date(selectedApplication.reviewed_at).toLocaleString()}
                    </p>
                    {selectedApplication.reviewed_by && (
                      <p className="text-xs text-gray-500">by {selectedApplication.reviewed_by}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-2" />
                    Applicant
                  </div>
                  <p className="font-medium">{selectedApplication.applicant_name}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-500">
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </div>
                  <p className="font-medium">{selectedApplication.email}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-500">
                    <Phone className="h-4 w-4 mr-2" />
                    Phone
                  </div>
                  <p className="font-medium">{selectedApplication.phone}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="h-4 w-4 mr-2" />
                    Country
                  </div>
                  <p className="font-medium">{selectedApplication.country}</p>
                </div>
                {selectedApplication.website && (
                  <div className="space-y-1">
                    <div className="flex items-center text-sm text-gray-500">
                      <Globe className="h-4 w-4 mr-2" />
                      Website
                    </div>
                    <a href={selectedApplication.website} target="_blank" rel="noopener noreferrer" className="font-medium text-blue-600 hover:underline">
                      {selectedApplication.website}
                    </a>
                  </div>
                )}
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 mr-2" />
                    Years in Business
                  </div>
                  <p className="font-medium">{selectedApplication.years_in_business}</p>
                </div>
              </div>

              {/* Business Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-500">
                    <Briefcase className="h-4 w-4 mr-2" />
                    Industry Focus
                  </div>
                  <p className="font-medium">{selectedApplication.industry_focus}</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center text-sm text-gray-500">
                    <Target className="h-4 w-4 mr-2" />
                    Est. Clients/Year
                  </div>
                  <p className="font-medium">{selectedApplication.estimated_clients_per_year}</p>
                </div>
              </div>

              {/* Client Base Description */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Client Base Description</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedApplication.client_base_description}</p>
              </div>

              {/* Why Partner */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Why They Want to Partner</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedApplication.why_partner}</p>
              </div>

              {/* Admin Notes */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Internal Notes</h4>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this application..."
                  rows={3}
                  className="mb-2"
                />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => saveNotes(selectedApplication.id)}
                  disabled={updating}
                >
                  Save Notes
                </Button>
              </div>

              {/* Metadata */}
              <div className="text-xs text-gray-400 pt-4 border-t">
                <p>Application ID: {selectedApplication.id}</p>
                <p>Submitted: {new Date(selectedApplication.submitted_at).toLocaleString()}</p>
                {selectedApplication.ip_address && <p>IP Address: {selectedApplication.ip_address}</p>}
              </div>

              {/* Actions */}
              {selectedApplication.status === 'pending' && (
                <div className="flex space-x-3 pt-4 border-t">
                  <Button
                    onClick={() => updateApplication(selectedApplication.id, 'approved')}
                    disabled={updating}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Application
                  </Button>
                  <Button
                    onClick={() => updateApplication(selectedApplication.id, 'rejected')}
                    disabled={updating}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Application
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnersManagement;
